/**
 * Created by neo on 13.10.2023
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NotFoundScreen } from '../NotFoundScreen';
import { ScanQrCodeScreen } from './ScanQrCodeScreen';
import { useEffect } from 'react';
import { ExploreContentViewRenderer } from '../Components/EntryView/ExploreContentViewRenderer';
import { ExploreContentBuilder, KinasticContent } from '../Model/Explore/ExploreContentBuilder';
import { ExploreCategory } from '../Model/Explore/ExploreCategory';
import { isCompanyRootCategory, canViewerAccessCompany } from '../Model/Explore/companyCategory';
import { ExploreTabLibraryScreen } from './ExploreTab/ExploreTabLibraryScreen';
import { useRootStore } from '../Store/useRootStore';
import { usePlausible } from '../Contexts/PlausibleContext';

const appLinkPrefixes = [
  '/explore/',
  '/article/',
  '/activityLog/',
  '/activityLogEdit/',
  '/activityLogEditByActivity/',
  '/challenges/',
  '/mindfulness-programs',
  '/health-data-point',
  '/mindfulness/unit/',
  '/workouts/',
  '/profile/settings/tracker',
  '/engagement/earn/pointsCollected',
  '/rewards',
  '/tracker/terra',
  '/feed',
  '/workouts',
  '/explore-tab/',
  '/company/',
  '/profile/setting',
  '/route-challenges',
];

const supportedTypes = [
  'article',
  'webinar',
  'activityWorkout',
  'onlineMeeting',
  'recipe',
  'meditation',
  'breathing',
  'workoutTemplate',
];

export type CatchAllRouteProps = {};

export const CatchAllRoute: React.FC<CatchAllRouteProps> = observer((props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { gymCustomer } = useRootStore();
  const { trackPlausible } = usePlausible();

  const [loading, setLoading] = React.useState(true);
  const [entry, setEntry] = React.useState<KinasticContent | undefined>(undefined);

  // determine slug from last path segment
  const parts = location.pathname
    .replace(/\/$/, '')
    .split('/')
    .filter((p) => p);
  const slug = parts[parts.length - 1];

  useEffect(() => {
    setEntry(undefined);
    if (!slug) {
      setLoading(false);
      return;
    }
    setLoading(true);
    ExploreContentBuilder.findOne(slug)
      .then((explore) => setEntry(explore))
      .catch(() => setEntry(undefined))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!loading && !entry && !appLinkPrefixes.some((prefix) => location.pathname.startsWith(prefix))) {
      trackPlausible('404');
    }
  }, [loading, entry, location.pathname, trackPlausible]);

  // EPIC-0009: when the catch-all resolves to a company-private root category,
  // redirect to the `/company` index (own gym) or `/` (cross gym) instead of
  // rendering ExploreTabLibraryScreen. This makes /explore-tab/<gymRootId> land
  // on the same branded index view that /company already produces, without
  // hardcoding anything screen-level at the explore-tab URL.
  //
  // Race-condition guard: gymCustomer.customer is fetched asynchronously by
  // GymCustomerStore after auth resolves, and the entry fetch usually wins the
  // race. If we computed redirectTarget while customer was still undefined,
  // canViewerAccessCompany would return false and the same-gym viewer would be
  // wrongly redirected to '/'. We gate the decision on `customer !== undefined`
  // so we wait for the customer to load before deciding own-gym vs cross-gym.
  const categoryEntry =
    entry && 'contentType' in entry && entry.contentType === 'category' && 'identifier' in entry
      ? (entry as ExploreCategory)
      : undefined;
  const viewerGymId = gymCustomer.customer?.gymId;
  const isCompanyRoot = isCompanyRootCategory(categoryEntry);
  const customerLoaded = gymCustomer.customer !== undefined;
  const redirectTarget = isCompanyRoot && customerLoaded
    ? canViewerAccessCompany(categoryEntry, viewerGymId)
      ? '/company'
      : '/'
    : null;

  useEffect(() => {
    if (redirectTarget) {
      navigate(redirectTarget, { replace: true });
    }
  }, [redirectTarget, navigate]);

  if (redirectTarget) {
    return null;
  }

  // Suppress the ExploreTabLibraryScreen flicker for a company-root URL while
  // we wait for the customer to load and the redirect to settle.
  if (isCompanyRoot && !customerLoaded) {
    return null;
  }

  if (!loading) {
    // entry page
    if (entry && supportedTypes.includes(entry.type)) {
      return <ExploreContentViewRenderer entry={entry} />;
    }
    // category page
    if (categoryEntry) {
      return <ExploreTabLibraryScreen category={categoryEntry} />;
    }
    // deep-link / QR scan fallback
    if (appLinkPrefixes.some((prefix) => location.pathname.startsWith(prefix))) {
      return <ScanQrCodeScreen />;
    }
    return <NotFoundScreen />;
  }

  return null;
});
